﻿import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import { useProductsAvailable, useSaveProductBulkCustomer, useBulkProductSuggestions } from "@/hooks/products/useProducts";
import Fetching from "@/components/Fetching/Fetching";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm.jsx";

import ProductBulkModeSelector from "./components/ProductBulkModeSelector.jsx";
import ProductBulkManualForm from "./components/ProductBulkManualForm.jsx";
import ProductBulkModalFooter from "./components/ProductBulkFooter.jsx";

import classes from "../../maintainers.module.scss";
import { formatUnitMeasure } from "@/services/Utils.jsx";

const normalizeUnitMeasurementFromFile = (unitValue) => {
	if (unitValue === undefined || unitValue === null) {
		return "";
	}

	const normalizedUnitValue = String(unitValue)
		.trim()
		.toLowerCase()
		.normalize("NFD")
		.replaceAll(/[\u0300-\u036f]/g, "")
		.replaceAll(".", "");

	const gramsAliases = ["g", "kg", "k", "kilo", "kilos", "kilogramo", "kilogramos", "kilogram", "kilograms"];
	const litersAliases = ["cc", "c", "l", "lt", "lts", "litro", "litros", "liter", "liters"];
	const unitsAliases = ["u", "un", "uni", "unidad", "unidades", "unit", "units"];

	if (gramsAliases.includes(normalizedUnitValue)) {
		return "g";
	}

	if (litersAliases.includes(normalizedUnitValue)) {
		return "cc";
	}

	if (unitsAliases.includes(normalizedUnitValue)) {
		return "u";
	}

	return formatUnitMeasure(normalizedUnitValue).toLowerCase() === "kg" ? "g" : "";
};

const ProductBulk = () => {
	const { option } = useParams();
	const navigate = useNavigate();

	const { data, isLoading } = useProductsAvailable(option);
	const { mutate: mutateAttach, isLoading: isLoadingAttach } = useSaveProductBulkCustomer();
	const { mutateAsync: fetchBulkSuggestions } = useBulkProductSuggestions();
	const [products, setProducts] = useState([]);
	const [uploadType, setUploadType] = useState("");
	const [previewData, setPreviewData] = useState(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const refFile = useRef(null);
	const { type_currency } = useSelector((state) => state.auth.filter);

	useEffect(() => {
		if (data && data.products) {
			setProducts(data.products);
		}
	}, [data]);

	const handleClose = () => {
		clearFields();
		navigate(-1);
	};

	const clearFields = () => {
		setUploadType("");
		setPreviewData(null);
		setIsProcessing(false);
	};

	const handleSave = () => {
		let products;
		if (["individual", "bulk"].includes(uploadType)) {
			products = previewData.map((item) => {
				const copy = { ...item };
				delete copy.text_product;
				delete copy.unit;

				if (copy.is_new) {
					delete copy.value;
				}
				return copy;
			});
		} else {
			products = previewData;
		}

		mutateAttach(
			{ attach_type: uploadType, products, currency: type_currency },
			{
				onSuccess: () => {
					setIsProcessing(false);
					handleClose();
					toast.success("Productos agregados correctamente");
					globalThis.dispatchEvent(new CustomEvent("products-bulk-saved"));
				},
				onError: (error) => {
					toast.error(error?.data || "Error al guardar productos");
				},
			},
		);
	};

	const handleFileChange = async (e) => {
		const inputElement = e.target;
		const file = inputElement.files?.[0];
		if (!file) return;
		setUploadType("bulk");
		setIsProcessing(true);

		try {
			const arrayBuffer = await file.arrayBuffer();
			const workbook = XLSX.read(arrayBuffer, { type: "array" });
			const firstSheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[firstSheetName];
			const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

			const processedData = jsonData
				.slice(1)
				.filter((rowData) => {
					const [product, unit] = rowData;
					return product && unit && product.toString().trim() !== "" && unit.toString().trim() !== "";
				})
				.map((rowData, index) => {
					const [product, unit] = rowData;
					const normalizedProduct = product.toString().trim();
					const normalizedUnitMeasurement = normalizeUnitMeasurementFromFile(unit);
					const existingProduct = products.find(
						(availableProduct) => availableProduct?.label?.toString().trim().toLowerCase() === normalizedProduct.toLowerCase(),
					);

					return {
						id: index + 1,
						value: existingProduct ? existingProduct.value : normalizedProduct,
						label: normalizedProduct,
						text_product: normalizedProduct,
						is_new: !existingProduct,
						unit: existingProduct?.unit_measurement || normalizedUnitMeasurement,
						unit_measurement: existingProduct?.unit_measurement || normalizedUnitMeasurement,
					};
				});

			const suggestionTerms = [...new Set(processedData.map((rowData) => rowData.label).filter(Boolean))];
			let suggestionsByTerm = {};

			if (suggestionTerms.length > 0) {
				try {
					const suggestionsResponse = await fetchBulkSuggestions(suggestionTerms);
					suggestionsByTerm = suggestionsResponse?.suggestions || {};
				} catch {
					suggestionsByTerm = {};
				}
			}

			const dataWithSuggestions = processedData.map((rowData) => ({
				...rowData,
				suggestions: suggestionsByTerm[rowData.label] || [],
			}));

			setPreviewData(dataWithSuggestions);
		} catch {
			toast.error("No se pudo procesar la planilla cargada");
		} finally {
			setIsProcessing(false);
			inputElement.value = "";
		}
	};

	const addProductField = () => {
		const newId = previewData ? Math.max(...previewData.map((p) => p.id), 0) + 1 : 1;
		const newItem = {
			id: newId,
			label: "",
			text_product: "",
			is_new: false,
			unit_measurement: "",
		};

		if (previewData) {
			setPreviewData((prev) => [...prev, newItem]);
		} else {
			setPreviewData([newItem]);
		}
	};

	const initializeIndividualForm = () => {
		setUploadType("individual");
		if (!previewData || previewData.length === 0) {
			addProductField();
		}
	};

	const validateProductForm = () => {
		if (!previewData || previewData.length === 0) return false;

		return previewData.every((item) => {
			const hasProduct = item.label;
			const needsUnit = item.is_new && !item.unit_measurement;
			return hasProduct && !needsUnit;
		});
	};

	const deleteProductField = (id) => {
		if (previewData && previewData.length > 1) {
			setPreviewData((prev) => prev.filter((input) => input.id !== id));
		} else {
			toast.error("Debe haber al menos un campo de producto");
		}
	};

	const updateProductData = (id, field, value) => {
		setPreviewData((prev) => prev.map((input) => (input.id === id ? { ...input, [field]: value } : input)));
	};

	const applyProductSuggestion = (id, suggestion) => {
		setPreviewData((prev) =>
			prev.map((input) =>
				input.id === id
					? {
							...input,
							value: suggestion.value,
							label: suggestion.label,
							text_product: suggestion.label,
							unit: suggestion.unit_measurement,
							unit_measurement: suggestion.unit_measurement,
							is_new: false,
						}
					: input,
			),
		);
	};

	const handleDownloadTemplate = () => {
		const templateData = [["Producto", "Unidad de medida"]];
		const worksheet = XLSX.utils.aoa_to_sheet(templateData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla");
		XLSX.writeFile(workbook, "plantilla_productos.xlsx");
	};

	const UNIT_MEASURE_OPTIONS = [
		{ value: "g", label: "Kg" },
		{ value: "cc", label: "L" },
		{ value: "u", label: "Unidad" },
	];

	return (
		<CustomModalForm
			closeOnEsc={false}
			showSave={false}
			open={Boolean(option && ["agregar"].includes(option))}
			onClose={handleClose}
			title="Carga de Productos"
			size={"lg"}
			footer={
				<ProductBulkModalFooter
					uploadType={uploadType}
					previewData={previewData}
					isLoading={isLoading}
					isLoadingAttach={isLoadingAttach}
					isProcessing={isProcessing}
					isIndividualFormValid={validateProductForm()}
					onBack={clearFields}
					onSave={handleSave}
					onClose={handleClose}
				/>
			}
		>
			{isLoading || isLoadingAttach ? (
				<Fetching topMargin={"1em"} />
			) : (
				<div className={classes.upload_products}>
					{uploadType ? null : (
						<ProductBulkModeSelector
							onFileUpload={handleFileChange}
							onIndividualUpload={initializeIndividualForm}
							onDownloadTemplate={handleDownloadTemplate}
							refFile={refFile}
						/>
					)}

					{isProcessing && (
						<div className={classes.loading}>
							<Fetching topMargin={"0.5em"} />
							<p>Procesando archivo...</p>
						</div>
					)}

					{["individual", "bulk"].includes(uploadType) && (
						<ProductBulkManualForm
							items={previewData || []}
							products={products}
							unitMeasureOptions={UNIT_MEASURE_OPTIONS}
							onUpdateProduct={updateProductData}
							onApplySuggestion={applyProductSuggestion}
							onDeleteProduct={deleteProductField}
							onAddProduct={addProductField}
							setProducts={setProducts}
						/>
					)}
				</div>
			)}
		</CustomModalForm>
	);
};

export default ProductBulk;
